import { Button, useDataProvider } from "react-admin";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import React, { useState } from "react";
import { useField } from "react-final-form";
import {
  colors,
  generateRouteName,
  removeSchoolLocationFromRouteLocation,
  versioningRoutePlan,
} from "../tools";
import SanitizedGrid from "../../../components/SanitizedGrid";
import CustomNumberInput from "../../../components/CustomNumberInput";
import { changeIsFinishFetchOptimisationRoute } from "../../../redux/navmode.action";
import { find } from "lodash";
import { useDispatch } from "react-redux";
import AiLoading from "./AiLoading";
import Alert from "@material-ui/lab/Alert";
import SparklingStarIcon from "../../../components/icons/SparklingStartIcon";

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
});

const groupLocationsFromAllRoutes = (routes: IRoute[], routeType: string) => {
  const locations = routes
    .map((route) =>
      removeSchoolLocationFromRouteLocation(route.routeLocations, routeType),
    )
    .reduce((acc, curr) => acc.concat(curr), []);

  // Group by location coordinates and merge studentIds
  const locationMap = new Map<string, IRouteLocation>();

  locations.forEach((location) => {
    const studentIds = Array.isArray(location.students)
      ? location.students.map((student) => student.id)
      : [];

    const precision = 6;
    const lat = parseFloat(location.lat.toFixed(precision));
    const lng = parseFloat(location.lng.toFixed(precision));

    // Use lat/lng as unique identifier
    const locationKey = `${lat},${lng}`;

    if (locationMap.has(locationKey)) {
      // Merge studentIds if location already exists
      const existingLocation = locationMap.get(locationKey)!;
      existingLocation.studentIds = Array.from(
        new Set([...existingLocation.studentIds, ...location.studentIds]),
      );
    } else {
      // Add new location to map
      locationMap.set(locationKey, { ...location, studentIds });
    }
  });

  return Array.from(locationMap.values()).map((location) => ({
    lat: location.lat,
    lng: location.lng,
    street: location.street,
    unitNumber: location.unitNumber,
    blockNumber: location.blockNumber,
    postalCode: location.postalCode,
    building: location.building,
    address: `${location.blockNumber ?? ""} ${location.street ?? ""} ${
      location.building ?? ""
    } ${location.postalCode ?? ""}`,
    studentIds: location.studentIds,
  }));
};

const getAllStudentsFromRoutes = (routes: IRoute[]) => {
  const allStudents = routes
    .flatMap((route) => route.routeLocations)
    .flatMap((location) => location.students || []);

  // Remove duplicates based on student ID
  const uniqueStudents = allStudents.filter(
    (student, index, self) =>
      index === self.findIndex((s) => s.id === student.id),
  );

  return uniqueStudents;
};

const ReadjustAllRouteButton = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const formRoutesChangedField = useField("formRoutesChanged");
  const routesField = useField("routes");
  const busScheduleField = useField("busSchedule");
  const droppedLocationsField = useField("droppedLocations");
  const schoolField = useField("school");
  const maxVehicleCapacityField = useField("maxVehicleCapacity");
  const desiredRouteDurationField = useField("desiredRouteDuration");
  const maxStopTimeField = useField("maxStopTime");
  const oldRouteIdsField = useField("oldRouteIds");

  const classes = useStyles();
  const provider = useDataProvider();
  const dispatch = useDispatch();

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setError(null);
  };

  const handleReadjustAllRoute = async () => {
    setIsLoading(true);
    setError(null);
    dispatch(changeIsFinishFetchOptimisationRoute(false));
    formRoutesChangedField.input.onChange(false);

    try {
      const locations = groupLocationsFromAllRoutes(
        routesField.input.value,
        busScheduleField.input.value.type,
      );

      const students = getAllStudentsFromRoutes(routesField.input.value);

      const oldRouteIds = routesField.input.value.map(
        (route: IRoute) => route.realRouteId,
      );

      let resultRoutes;
      let droppedLocations;

      if (busScheduleField.input.value.type === "to_school") {
        const requestJson = {
          destinationLocation: {
            lat: schoolField.input.value.lat,
            lng: schoolField.input.value.lng,
            street: schoolField.input.value.street,
            unitNumber: schoolField.input.value.unitNumber,
            blockNumber: schoolField.input.value.blockNumber,
            building: schoolField.input.value.building,
            address: schoolField.input.value.address,
            postalCode: schoolField.input.value.postalCode,
          },
          desiredArrivalTime: busScheduleField.input.value.arrivalTime
            .split(":")
            .slice(0, 2)
            .join(":"),
          maxVehicleCapacity: Number(maxVehicleCapacityField.input.value),
          desiredRouteDuration: desiredRouteDurationField.input.value * 60,
          maxStopTime: maxStopTimeField.input.value * 60,
          locations: locations,
        };

        const responseToSchool = await provider.customCreate(
          "routeOptimization/pickUp",
          {
            data: requestJson,
          },
        );

        resultRoutes = JSON.parse(JSON.stringify(responseToSchool.data.routes));
        droppedLocations = JSON.parse(
          JSON.stringify(responseToSchool.data.droppedLocations),
        );
      } else if (
        busScheduleField.input.value.type === "from_school" ||
        busScheduleField.input.value.type === "cca"
      ) {
        const requestJson = {
          originLocation: {
            lat: schoolField.input.value.lat,
            lng: schoolField.input.value.lng,
            street: schoolField.input.value.street,
            unitNumber: schoolField.input.value.unitNumber,
            blockNumber: schoolField.input.value.blockNumber,
            building: schoolField.input.value.building,
            address: schoolField.input.value.address,
            postalCode: schoolField.input.value.postalCode,
          },
          desiredDepartureTime: busScheduleField.input.value.arrivalTime
            .split(":")
            .slice(0, 2)
            .join(":"),
          maxVehicleCapacity: Number(maxVehicleCapacityField.input.value),
          desiredRouteDuration: desiredRouteDurationField.input.value * 60,
          maxStopTime: maxStopTimeField.input.value * 60,
          locations: locations,
        };

        const responseFromSchool = await provider.customCreate(
          "routeOptimization/dropOff",
          {
            data: requestJson,
          },
        );

        resultRoutes = JSON.parse(
          JSON.stringify(responseFromSchool.data.routes),
        );
        droppedLocations = JSON.parse(
          JSON.stringify(responseFromSchool.data.droppedLocations),
        );
      }

      const transformedRoutes = resultRoutes.map(
        (route: any, index: number) => {
          const routeName = generateRouteName(
            busScheduleField.input.value.type,
            index + 1,
          );

          return {
            id: index + 1,
            realRouteId: 0,
            routeName: routeName,
            startTime: "",
            endTime: "",
            busBayNumber: null,
            driverId: null,
            busAssistantId: null,
            vehicleId: null,
            backupDriverIds: [],
            backupBusAssistantIds: [],
            backupVehicleIds: [],
            assignedSubcontractor: null,
            routeColor: colors[index % colors.length], // if colors are not enough, roll over the colors array to first index
            routeGeometries: [],
            routeLocations: route.locations.map(
              (location: any, index: number) => {
                let locationCodeIndex;

                if (busScheduleField.input.value.type === "to_school") {
                  locationCodeIndex = index;
                } else {
                  locationCodeIndex = index - 1;
                }

                // convert ASCII Code into Alphabet
                // 65 === A
                const locationCode = String.fromCharCode(
                  65 + locationCodeIndex,
                );

                return {
                  id: 0,
                  lat: location.lat,
                  lng: location.lng,
                  street: location.street,
                  unitNumber: location.unitNumber,
                  blockNumber: location.blockNumber,
                  building: location.building,
                  address: location.address,
                  postalCode: location.postalCode,
                  order:
                    busScheduleField.input.value.type === "to_school"
                      ? index + 1
                      : index,
                  arrivalTime: "",
                  remark: null,
                  students: location.studentIds
                    ? location.studentIds.map(
                        (studentId: any, index: number) => {
                          const findStudent = find(students, ["id", studentId]);

                          return {
                            ...findStudent,
                            locationCode: `${locationCode}${index + 1}`,
                          };
                        },
                      )
                    : [],
                } as any;
              },
            ),
          } as any;
        },
      );

      const transformedDroppedLocations = droppedLocations.map(
        (location: any, index: number) => ({
          id: index + 1,
          lat: location.lat,
          lng: location.lng,
          street: location.street,
          unitNumber: location.unitNumber,
          blockNumber: location.blockNumber,
          building: location.building,
          address: location.address,
          postalCode: location.postalCode,
          order: "",
          arrivalTime: "",
          students: location.studentIds
            ? location.studentIds.map((studentId: any) => {
                const findStudent = find(students, ["id", studentId]);

                return {
                  ...findStudent,
                  locationCode: "",
                };
              })
            : [],
        }),
      );

      // versioning route plan
      versioningRoutePlan(transformedRoutes, transformedDroppedLocations);

      routesField.input.onChange(transformedRoutes);
      droppedLocationsField.input.onChange(transformedDroppedLocations);
      oldRouteIdsField.input.onChange(oldRouteIds);

      handleClose();
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setIsLoading(false);
      dispatch(changeIsFinishFetchOptimisationRoute(true));
    }
  };

  return (
    <>
      <Button
        label="Re-adjust All Routes with AI"
        onClick={handleOpenDialog}
        alignIcon="right"
      >
        <SparklingStarIcon
          style={{
            width: "24px",
            height: "24px",
            verticalAlign: "middle",
            color: "#FFD700",
          }}
        />
      </Button>

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">
          Readjust All Routes With AI
        </DialogTitle>
        <DialogContent>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2">
                AI Route Optimisation Parameter
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <CustomNumberInput
                label="Desired Route Duration (Minutes)"
                source="desiredRouteDuration"
                isRequired
                isPositiveValue
              />
            </Grid>

            <Grid item xs={12}>
              <CustomNumberInput
                label="Max Vehicle Capacity"
                source="maxVehicleCapacity"
                isRequired
                isPositiveValue
              />
            </Grid>

            <Grid item xs={12}>
              <CustomNumberInput
                label="Max Bus Stop Duration (Minutes)"
                source="maxStopTime"
                isRequired
                isPositiveValue
              />
            </Grid>

            <Grid item xs={12}>
              <DialogContentText color="initial">
                <>
                  This action will readjust all existing routes to the system's
                  recommended route by AI.
                  <br />
                  Are you sure you want to readjust all routes?
                </>
              </DialogContentText>
            </Grid>

            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
          </SanitizedGrid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" label="Back" />
          <Button
            onClick={handleReadjustAllRoute}
            color="primary"
            label="Confirm"
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={isLoading}
        aria-labelledby="ai-loading-title"
        aria-describedby="ai-loading-description"
        maxWidth="md"
      >
        <AiLoading isLoading={isLoading} />
      </Dialog>
    </>
  );
};

export default ReadjustAllRouteButton;
